import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { t } from "i18next";
import { ReactNode, useRef, useState } from "react";
import { FaCopy, FaEye, FaEyeSlash, FaKey, FaTrash } from "react-icons/fa";
import {
  bffApi,
  EntraConnectionDto,
  EntraDefaultRoleScope,
  EntraGroupDto,
  OrganizationRoleType,
  OrgUserInviteRole,
  useDeleteEntraConnectionMutation,
  useDeleteEntraGroupMappingMutation,
  useSaveEntraGroupMappingMutation,
  useUpdateEntraConnectionMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import {
  EntraDefaultRoleModal,
  useDefaultRoleWhere,
  useInviteRoleName,
} from "./EntraDefaultRoleModal";
import { offersDefaultDepartment } from "./entraDefaultRoleScope";
import { hasEverSynced } from "./entraSyncState";
import { EntraGroupTable } from "./EntraGroupTable";

const ROW_LABEL_WIDTH = "220px";

const SectionHeading = ({ children }: { children: ReactNode }) => (
  <Text
    fontSize="xs"
    fontWeight="bold"
    color="smMuted"
    textTransform="uppercase"
    letterSpacing="wider"
  >
    {children}
  </Text>
);

interface Props {
  organizationId: string;
  connection: EntraConnectionDto;
  departments: { id: string; name: string; active: boolean }[];
  defaultDepartmentName: string;
  subscription: { hasBuyerAccess: boolean };
}

export const EntraTenantCard = ({
  organizationId,
  connection,
  departments,
  defaultDepartmentName,
  subscription,
}: Props) => {
  const toast = useToast();
  const { triggerSoft } = useApiError();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const roleName = useInviteRoleName();
  const where = useDefaultRoleWhere(defaultDepartmentName);
  const choosesWhere = offersDefaultDepartment(connection.jitDefaultRoleScope, subscription);
  const [isKeyVisible, setIsKeyVisible] = useState(false);
  const [isEditingDefaultRole, setIsEditingDefaultRole] = useState(false);

  const [getKey, { data: key, isFetching: isLoadingKey }] =
    bffApi.endpoints.getTokenForConnection.useLazyQuery();
  const [saveMapping, { isLoading: isSaving }] =
    useSaveEntraGroupMappingMutation();
  const [deleteMapping, { isLoading: isRemoving }] =
    useDeleteEntraGroupMappingMutation();
  const [updateConnection, { isLoading: isUpdating }] =
    useUpdateEntraConnectionMutation();
  const [disconnect, { isLoading: isDisconnecting }] =
    useDeleteEntraConnectionMutation();

  const isBusy = isSaving || isRemoving || isUpdating || isDisconnecting;
  const synced = hasEverSynced(connection);
  const affected = connection.groups
    .filter((group) => group.mapping)
    .reduce((total, group) => total + group.memberCount, 0);
  const setRole = async (
    group: EntraGroupDto,
    role: OrganizationRoleType,
    departmentId: string | null,
  ): Promise<boolean> => {
    try {
      await saveMapping({
        orgId: organizationId,
        connectionId: connection.id,
        saveEntraGroupMappingRequest: {
          userGroupId: group.userGroupId,
          role,
          departmentId,
        },
      }).unwrap();
      return true;
    } catch (e) {
      triggerSoft(e);
      return false;
    }
  };

  const removeMapping = async (mappingId: string) => {
    try {
      await deleteMapping({
        orgId: organizationId,
        connectionId: connection.id,
        mappingId,
      }).unwrap();
    } catch (e) {
      triggerSoft(e);
    }
  };

  const setDefaultRole = async (
    defaultRole: OrgUserInviteRole,
    defaultRoleScope: EntraDefaultRoleScope,
  ) => {
    try {
      await updateConnection({
        orgId: organizationId,
        connectionId: connection.id,
        updateEntraConnectionRequest: { defaultRole, defaultRoleScope },
      }).unwrap();
      setIsEditingDefaultRole(false);
    } catch (e) {
      triggerSoft(e);
    }
  };

  const setJitEnabled = async (jitEnabled: boolean) => {
    try {
      await updateConnection({
        orgId: organizationId,
        connectionId: connection.id,
        updateEntraConnectionRequest: { jitEnabled },
      }).unwrap();
    } catch (e) {
      triggerSoft(e);
    }
  };

  const removeTenant = async () => {
    try {
      await disconnect({
        orgId: organizationId,
        connectionId: connection.id,
      }).unwrap();
    } catch (e) {
      triggerSoft(e);
    }
  };

  return (
    <Box
      mt="5"
      width="full"
      rounded="lg"
      border="1px solid"
      borderColor="smBorder"
      overflow="hidden"
    >
      <Flex
        alignItems="center"
        gap="3"
        flexWrap="wrap"
        px="5"
        py="3"
        backgroundColor="smBackgroundSecondary"
        borderBottom="1px solid"
        borderColor="smBorder"
      >
        <Text fontWeight="semibold">{t("Entra tenant")}</Text>
        <Text fontSize="xs" fontFamily="mono" color="smMuted">
          {connection.tenantId}
        </Text>
        <Badge colorScheme={synced ? "teal" : "gray"}>
          {synced ? t("Connected") : t("Waiting for Entra ID")}
        </Badge>
        <Flex flex="1" />
        <IconButton
          aria-label={t("Disconnect this tenant")}
          icon={<FaTrash />}
          size="sm"
          variant="ghost"
          colorScheme="red"
          isDisabled={isBusy}
          onClick={onOpen}
        />
      </Flex>

      <Box px="5" py="4" borderBottom="1px solid" borderColor="smBorder">
        <SectionHeading>{t("Connection to Entra ID")}</SectionHeading>
        <Flex direction="column" gap="3" mt="3">
          <FormControl
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            columnGap="6"
            rowGap="2"
          >
            <FormLabel
              m="0"
              width={ROW_LABEL_WIDTH}
              fontSize="sm"
              fontWeight="medium"
            >
              {t("Provisioning key")}
            </FormLabel>
            <Flex gap="2" alignItems="center" flex="1" flexWrap="wrap">
              {!key && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="teal"
                    leftIcon={<FaKey />}
                    isDisabled={isLoadingKey}
                    onClick={() => getKey({ connectionId: connection.id })}
                  >
                    {isLoadingKey ? <Spinner size="sm" /> : t("Get Secret")}
                  </Button>
                  <Text fontSize="sm" color="smMuted">
                    {t(
                      "Paste it into Entra ID when setting up provisioning for the SourceMagnet app.",
                    )}
                  </Text>
                </>
              )}
              {key && (
                <>
                  <Input
                    size="sm"
                    isReadOnly
                    fontFamily="mono"
                    type={isKeyVisible ? "text" : "password"}
                    value={key.token}
                  />
                  <IconButton
                    aria-label={t("Copy the provisioning key")}
                    icon={<FaCopy />}
                    size="sm"
                    variant="ghost"
                    colorScheme="teal"
                    onClick={() => {
                      void navigator.clipboard.writeText(key.token);
                      toast({
                        status: "success",
                        description: t("Secret copied to the clip board."),
                      });
                    }}
                  />
                  <IconButton
                    aria-label={
                      isKeyVisible
                        ? t("Hide the provisioning key")
                        : t("Show the provisioning key")
                    }
                    icon={isKeyVisible ? <FaEyeSlash /> : <FaEye />}
                    size="sm"
                    variant="ghost"
                    colorScheme="teal"
                    onClick={() => setIsKeyVisible(!isKeyVisible)}
                  />
                </>
              )}
            </Flex>
          </FormControl>
          <Flex flexWrap="wrap" alignItems="center" columnGap="6" rowGap="2">
            <Text width={ROW_LABEL_WIDTH} fontSize="sm" fontWeight="medium">
              {t("Login")}
            </Text>
            <Checkbox
              isChecked={!connection.jitEnabled}
              isDisabled={isBusy}
              onChange={(e) => void setJitEnabled(!e.target.checked)}
            >
              <Text fontSize="sm">
                {t("Only allow people provisioned from Entra ID to log in")}
              </Text>
            </Checkbox>
          </Flex>
        </Flex>
      </Box>

      <Box px="5" pt="4">
        <SectionHeading>{t("Access in SourceMagnet")}</SectionHeading>
        <Flex
          mt="3"
          px="4"
          py="3"
          border="1px solid"
          borderColor="smBorder"
          borderRadius="md"
          flexWrap="wrap"
          alignItems="center"
          columnGap="6"
          rowGap="2"
        >
          <Box width={ROW_LABEL_WIDTH}>
            <Text fontSize="sm" fontWeight="medium">
              {t("Default role for Entra ID users")}
            </Text>
          </Box>
          <Box flex="1">
            <Text fontSize="sm">
              {choosesWhere
                ? t("{{role}} in {{where}}", {
                    role: roleName(connection.jitDefaultRole),
                    where: where(connection.jitDefaultRoleScope),
                  })
                : roleName(connection.jitDefaultRole)}
            </Text>
            <Text fontSize="xs" color="smMuted">
              {t("{{count}} people have access through the default role", {
                count: connection.jitDefaultRoleHolders,
              })}
            </Text>
          </Box>
          <Button
            size="xs"
            variant="outline"
            colorScheme="teal"
            isDisabled={isBusy}
            onClick={() => setIsEditingDefaultRole(true)}
          >
            {t("Change")}
          </Button>
        </Flex>
      </Box>

      <EntraGroupTable
        groups={connection.groups}
        departments={departments}
        isBusy={isBusy}
        onSetRole={setRole}
        onRemove={(mappingId) => void removeMapping(mappingId)}
      />

      {isEditingDefaultRole && (
        <EntraDefaultRoleModal
          role={connection.jitDefaultRole}
          scope={connection.jitDefaultRoleScope}
          holders={connection.jitDefaultRoleHolders}
          defaultDepartmentName={defaultDepartmentName}
          offersDefaultDepartment={choosesWhere}
          isBusy={isUpdating}
          onSave={setDefaultRole}
          onClose={() => setIsEditingDefaultRole(false)}
        />
      )}

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t("Disconnect this tenant")}
            </AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              {t(
                "{{people}} people get their access from this tenant's groups and will lose it. Entra ID keeps the groups; only what they give here goes away",
                { people: affected },
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                size="sm"
                variant="ghost"
                onClick={onClose}
              >
                {t("Cancel")}
              </Button>
              <Button
                size="sm"
                colorScheme="red"
                ml="3"
                isDisabled={isBusy}
                onClick={() => {
                  onClose();
                  void removeTenant();
                }}
              >
                {t("Disconnect")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
